import { useState } from 'react';
import { fmt } from '../../lib/nmtc/calculations';

const TIMELINE_ITEMS = [
  { label: 'Closing (Month 0)', desc: 'QLICI loans originated. Compliance period begins.', highlight: null },
  { label: 'Year 1 (Month 12)', desc: 'First QALICB annual certification filed.', highlight: null },
  { label: 'Years 2–6 (Annual)', desc: 'Ongoing certifications + interest-only payments.', highlight: null },
  { label: 'Year 6 — Start Planning (Month 60–66)', desc: 'Engage unwind counsel. Source A-Note refinance.', highlight: 'amber' },
  { label: 'Year 7 — Unwind (Month 84)', desc: 'Compliance period ends. All documents executed simultaneously.', highlight: 'emerald' },
  { label: 'Post-Unwind', desc: 'B-Note forgiven. A-Note refinanced. Tax returns reflect COD.', highlight: null },
];

const UNWIND_STEPS = [
  { title: 'Engage Counsel (Year 6)', body: 'At least 12 months before Year 7, engage NMTC-experienced legal counsel. The unwind requires simultaneous execution of 8–12 documents. This is not a DIY process. Budget $15,000–$40,000.' },
  { title: 'Source A-Note Refinancing', body: 'The A-Note is not forgiven — only the B-Note is. You need conventional financing at Year 7 market rates to repay or refinance the A-Note. Start this process in Year 5 or 6.' },
  { title: 'Tax Credit Investor Exit', body: 'The investor sells its membership interest in the Investment Fund LLC to you for nominal consideration (typically $1). Documented with a Membership Interest Purchase Agreement.' },
  { title: 'B-Note Forgiveness', body: 'The CDE formally forgives the B-Note principal and interest with a Note Forgiveness Agreement and release of the associated security interest.' },
  { title: 'QLICI Satisfaction & Lien Release', body: 'CDE provides payoff statements. UCC financing statements and any mortgage/deed of trust securing the notes are formally released.' },
  { title: 'Investment Fund Dissolution', body: 'The Investment Fund LLC is typically dissolved after the investor exit. Remaining assets are distributed to you or collapsed into your entity.' },
];

const COMMON_MISTAKES = [
  'Assuming B-Note forgiveness is automatic (it requires affirmative action and executed documents)',
  "Not planning for the A-Note refinance (it doesn't get forgiven)",
  'Failing to maintain annual QALICB certifications (breach can trigger investor recapture rights)',
  'Using general counsel instead of NMTC-specialized counsel',
  'Not modeling COD income in Year 7 financial projections (for-profit)',
  'Assuming the CDE will still exist at Year 7 (CDEs get acquired or lose CDFI certification)',
];

const YEAR7_DOCS = [
  { doc: 'Membership Interest Purchase Agreement', purpose: "Transfers investor's Fund interest to borrower", nonprofitOnly: false },
  { doc: 'Note Forgiveness Agreement', purpose: 'CDE forgives B-Note', nonprofitOnly: false },
  { doc: 'Release of Lien / UCC Termination', purpose: 'Releases security interests on both notes', nonprofitOnly: false },
  { doc: 'QLICI Payoff/Satisfaction Statement', purpose: 'Confirms all loan obligations satisfied', nonprofitOnly: false },
  { doc: 'Investment Fund Operating Agreement Amendment', purpose: 'Updates membership post-exit', nonprofitOnly: false },
  { doc: 'Board/Member Resolutions', purpose: 'Authorizes unwind transactions', nonprofitOnly: false },
  { doc: 'IRS Form 1099-C (for-profit only)', purpose: 'Reports COD income amount', nonprofitOnly: true },
];

function dotClass(highlight) {
  if (highlight === 'emerald') return 'relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold bg-emerald-600 text-white ring-2 ring-emerald-200';
  if (highlight === 'amber') return 'relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold bg-amber-500 text-white ring-2 ring-amber-200';
  return 'relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold bg-slate-400 text-white ring-2 ring-slate-100';
}

function cardClass(highlight) {
  if (highlight === 'emerald') return 'mt-3 px-2 py-2 rounded-lg text-center bg-emerald-50 border border-emerald-200 w-full';
  if (highlight === 'amber') return 'mt-3 px-2 py-2 rounded-lg text-center bg-amber-50 border border-amber-200 w-full';
  return 'mt-3 px-2 py-2 rounded-lg text-center bg-white border border-slate-200 w-full';
}

function labelClass(highlight) {
  if (highlight === 'emerald') return 'text-xs font-semibold text-emerald-800 leading-snug';
  if (highlight === 'amber') return 'text-xs font-semibold text-amber-800 leading-snug';
  return 'text-xs font-semibold text-slate-700 leading-snug';
}

export default function UnwindMechanicsTab({ calc, isNonProfit }) {
  const [showStrategies, setShowStrategies] = useState(false);

  return (
    <div className="space-y-8">

      {/* Section A: Timeline */}
      <section>
        <h2 className="text-base font-semibold text-slate-800 mb-4">7-Year Compliance Timeline</h2>

        {/* Desktop horizontal */}
        <div className="hidden md:flex items-start gap-0 relative">
          {TIMELINE_ITEMS.map((item, i) => (
            <div key={i} className="flex-1 flex flex-col items-center relative">
              {i < TIMELINE_ITEMS.length - 1 && (
                <div className="absolute top-4 left-1/2 w-full h-0.5 bg-slate-300 z-0" />
              )}
              <div className={dotClass(item.highlight)}>{i + 1}</div>
              <div className={cardClass(item.highlight)}>
                <p className={labelClass(item.highlight)}>{item.label}</p>
                <p className="text-xs text-slate-500 mt-1 leading-snug">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile vertical */}
        <div className="flex md:hidden flex-col gap-0">
          {TIMELINE_ITEMS.map((item, i) => (
            <div key={i} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className={dotClass(item.highlight).replace('relative z-10 ', '')}>{i + 1}</div>
                {i < TIMELINE_ITEMS.length - 1 && <div className="w-0.5 flex-1 bg-slate-300 my-1" />}
              </div>
              <div className={`flex-1 pb-4 p-3 rounded-lg mb-2 ${item.highlight === 'emerald' ? 'bg-emerald-50 border border-emerald-200' : item.highlight === 'amber' ? 'bg-amber-50 border border-amber-200' : 'bg-white border border-slate-200'}`}>
                <p className={`text-sm font-semibold ${item.highlight === 'emerald' ? 'text-emerald-800' : item.highlight === 'amber' ? 'text-amber-800' : 'text-slate-700'}`}>{item.label}</p>
                <p className="text-sm text-slate-500 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Section B: Key Numbers */}
      <section>
        <h2 className="text-base font-semibold text-slate-800 mb-3">Key Numbers</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 flex flex-col gap-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">B-Note to be forgiven</p>
            <p className="text-2xl font-bold text-emerald-900 mt-1">{fmt(calc.bNote)}</p>
            <p className="text-sm text-emerald-700 mt-1">This amount will be forgiven at Year 7</p>
          </div>
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-5 flex flex-col gap-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">A-Note balance at Year 7</p>
            <p className="text-2xl font-bold text-amber-900 mt-1">{fmt(calc.aNote)}</p>
            <p className="text-sm text-amber-700 mt-1 font-medium">This is NOT forgiven. Plan to refinance or repay.</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5 flex flex-col gap-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Est. unwind legal costs</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{fmt(calc.unwindCostEstimate)}</p>
            <p className="text-sm text-slate-500 mt-1">Budget $15K–$40K for unwind counsel and documents</p>
          </div>
        </div>
      </section>

      {/* Section C: Tax Implications */}
      <section>
        <h2 className="text-base font-semibold text-slate-800 mb-3">Tax Implications at Unwind</h2>
        {isNonProfit === true ? (
          <div className="rounded-xl border border-green-300 bg-green-50 px-5 py-4">
            <p className="text-sm font-semibold text-green-800 mb-1">No COD Income — Non-Profit Entity</p>
            <p className="text-sm text-green-700 leading-relaxed">
              As a non-profit, B-Note forgiveness does not create taxable income — IRC §108(a)(1)(A) excludes COD income for tax-exempt entities.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-amber-300 bg-amber-50 px-5 py-4">
              <p className="text-sm font-semibold text-amber-900 mb-2">Tax Alert — For-Profit Borrowers</p>
              <p className="text-sm text-amber-800 leading-relaxed">
                When the B-Note of <strong>{fmt(calc.bNote)}</strong> is forgiven at Year 7, you will
                recognize approximately <strong>{fmt(calc.codIncomeEstimate)}</strong> in ordinary income.
                At a combined 30% rate, estimated tax liability is approximately <strong>{fmt(calc.codTaxEstimate)}</strong>.
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <button
                onClick={() => setShowStrategies((s) => !s)}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors"
              >
                <span className="text-sm font-semibold text-slate-800">Tax Planning Strategies</span>
                <span className="text-slate-400 text-lg leading-none">{showStrategies ? '−' : '+'}</span>
              </button>

              {showStrategies && (
                <div className="px-5 pb-5 border-t border-slate-100 space-y-3 pt-4">
                  {[
                    { title: 'Insolvency Exception (IRC §108(a)(1)(B))', body: 'If the borrower entity is technically insolvent at the time of forgiveness, COD income may be excluded. Requires careful balance sheet management.' },
                    { title: 'Qualified Real Property Indebtedness (IRC §108(c))', body: 'For real property businesses, may allow exclusion in exchange for a basis reduction. Only available for C corporations and individuals.' },
                    { title: 'Tax-Exempt Subsidiary Structure', body: 'Some for-profit borrowers structure the QALICB as a nonprofit or tax-exempt subsidiary to avoid COD income entirely. Complex but common on larger deals.' },
                  ].map((s, i) => (
                    <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm font-semibold text-slate-800 mb-1">{s.title}</p>
                      <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Section D: Step-by-Step */}
      <section>
        <h2 className="text-base font-semibold text-slate-800 mb-3">Step-by-Step Unwind Process</h2>
        <div className="space-y-3">
          {UNWIND_STEPS.map((step, i) => (
            <div key={i} className="flex gap-4 rounded-xl border border-slate-200 bg-white p-4">
              <div className="shrink-0 w-7 h-7 rounded-full bg-emerald-900 text-white flex items-center justify-center text-xs font-bold">
                {i + 1}
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">{step.title}</p>
                <p className="text-sm text-slate-600 mt-0.5 leading-relaxed">{step.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Section E: Documents Checklist */}
      <section>
        <h2 className="text-base font-semibold text-slate-800 mb-3">Year 7 Documents Checklist</h2>
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-emerald-900 text-white">
                <th className="px-4 py-3 text-left font-semibold">Document</th>
                <th className="px-4 py-3 text-left font-semibold">Purpose</th>
              </tr>
            </thead>
            <tbody>
              {YEAR7_DOCS.filter((d) => !d.nonprofitOnly || isNonProfit !== true).map((d, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="px-4 py-2.5 text-slate-800 font-medium">{d.doc}</td>
                  <td className="px-4 py-2.5 text-slate-600">{d.purpose}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Section F: Common Mistakes */}
      <section>
        <h2 className="text-base font-semibold text-slate-800 mb-3">Common Mistakes to Avoid</h2>
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
          <ul className="space-y-2">
            {COMMON_MISTAKES.map((mistake, i) => (
              <li key={i} className="flex gap-2 items-start">
                <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-amber-400 text-white flex items-center justify-center text-xs font-bold">!</span>
                <span className="text-sm text-amber-900 leading-relaxed">{mistake}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
